// (c) Copyright 2024-2025 Hewlett Packard Enterprise Development LP
import { Box, Text } from "grommet"
import { ErrorBoundary } from "react-error-boundary"
import { useTranslation } from "react-i18next"
import styled from "styled-components"
import BrandButton from "./brand-button"
import MiniLaunchPad from "./mini-launchpad"
import { getTestIdAttribute, header } from "../utils/test-ids"
import { isROP } from "../utils/env"

const Pipe = () => (
  <Text
    color="text-weak"
    margin={{ horizontal: "8px" }}
    style={{ userSelect: "none" }}
  >
    |
  </Text>
)

// Allows child elements to query its width
const Container = styled.div`
  container-type: inline-size;
`

/**
 * Padding style below matches the padding of DSCC header with padding of GLP header
 * This ensures that GLP header and DSCC header icons are vertically aligned
 */
const StyledHeader = styled(Box)`
  @container (min-width: 769px) {
    padding-left: 24px;
    padding-right: 24px;
  }
  @container (min-width: 1440px) {
    padding-left: 48px;
    padding-right: 48px;
  }
`

export default function ShellHeader({
  brandTitle,
  children,
  dataTestId = header.container,
  serviceTitle,
}) {
  const { t } = useTranslation()

  return (
    <Container>
      <StyledHeader
        aria-label={isROP() ? "ROP Header" : "DSCC Header"}
        border={{ side: "bottom", color: "border-weak", size: "1px" }}
        align="center"
        color="background"
        direction="row"
        gap="medium"
        height="72px"
        justify="between"
        pad={{ horizontal: "12px" }}
        role="region"
        {...getTestIdAttribute(dataTestId)}
      >
        <Box align="center" direction="row">
          <BrandButton>{brandTitle}</BrandButton>
          {serviceTitle && (
            <>
              <Pipe />
              <ErrorBoundary
                fallback={<Text color="text-strong">{t(serviceTitle)}</Text>}
              >
                <MiniLaunchPad serviceTitle={serviceTitle} />
              </ErrorBoundary>
            </>
          )}
          {/* REGION SHOWCASE CODE ONLY */}
          <Box
            align="center"
            background="background-contrast"
            border={{ color: "text-weak", size: "1px" }}
            round="xsmall"
            pad={{ horizontal: "8px", vertical: "4px" }}
            margin={{ left: "auto" }}
          >
            <Text color="text-strong">us-east</Text>
          </Box>
        </Box>
        {children && (
          <Box align="center" cssGap direction="row" gap="6px">
            {children}
          </Box>
        )}
      </StyledHeader>
    </Container>
  )
}
